import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import Button from '../../../../components/form/button/Button';
import { spawnGlobalNotification } from '../../../../core/state/global-notifications/asyncActions';
import { GlobalNotificationTypes } from '../../../../core/enums/GlobalNotificationTypes';
import { RootState } from '../../../../core/state/state';
import { Color } from '../../../../core/enums/Color';
import TBody from '../../../../components/table/TBody/TBody';
import Table from '../../../../components/table/Table';
import TR from '../../../../components/table/TR/TR';
import TD from '../../../../components/table/TD/TD';
import deliveryZoneColorMap from '../../../../core/constants/deliveryZoneColorMap';
import Icon from '../../../../components/icon/Icon';
import THead from '../../../../components/table/THead/THead';
import TH from '../../../../components/table/TH/TH';
import Badge from '../../../components/badge/Badge';
import { generateEntityFetchListAsyncAction } from '../../../../core/state/entity/generateEntityAsyncActions';
import EntityName from '../../../../core/enums/EntityName';
import { NotificationPosition } from '../../../../core/state/global-notifications/types';
import { cancelActiveBookings } from '../../../../core/state/bookings/asyncActions';
import Modal from '../../../components/modal/Modal';

import './CancelAllBookingsModal.scss';

interface Props {
  isOpen: boolean;
  closeModal: () => void;
  type?: string;
}

const CancelAllBookingsModal: React.FC<Props> = ({ isOpen, closeModal, type }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fetchBlockedSlots = React.useMemo(() => generateEntityFetchListAsyncAction(EntityName.BlockedSlot), []);
  const { projectId } = useParams<{ projectId: string }>();
  const blockedBookings = useSelector((state: RootState) => state.bookings.blockedBookings);

  const onSubmit = async () => {
    const bookingIds = blockedBookings?.map((item) => item.id).toString();
    if (bookingIds) {
      await dispatch<any>(cancelActiveBookings(bookingIds, type));
      await dispatch(fetchBlockedSlots({ projectId }));
      dispatch(
        spawnGlobalNotification({
          postition: NotificationPosition.Top,
          type: GlobalNotificationTypes.Success,
          message: t('modal.cancelAllBookings.form.message.success')
        })
      );
    }

    closeModal();
  };

  const buttons = (
    <div className="cancel-all-bookings-modal__notification-actions">
      <Button className="cancel-all-bookings-modal__notification-action" color={Color.Red} buttonTypes={['transparent']} onClick={onSubmit}>
        {t('modal.cancelAllBookings.button.cancelBookings')}
      </Button>
      <Button className="cancel-all-bookings-modal__notification-action" buttonTypes={['transparent']} onClick={closeModal}>
        {t('common.cancel')}
      </Button>
    </div>
  );

  return (
    <Modal className="modal modal--large cancel-all-bookings-modal" isOpen={isOpen} closeModal={closeModal}>
      <div className="cancel-all-bookings-modal__notification">
        <div className="cancel-all-bookings-modal__notification-content">
          <div>
            <Icon className="cancel-all-bookings-modal__notification-icon" icon="alert" svg size="small" />
          </div>
          <div className="d-flex flex-column">
            <span className="global-notification__title">{t('modal.cancelAllBookings.notification.title')}</span>
            <span>{t('modal.cancelAllBookings.notification.text')}</span>
          </div>
        </div>
        {buttons}
      </div>
      <Table className="cancel-all-bookings-modal__table">
        <THead>
          <TR>
            <TH>
              <span>{t('booking.booking')}</span>
            </TH>
            <TH>
              <span>{t('common.date')}</span>
            </TH>
            <TH>
              <span>{t('common.time')}</span>
            </TH>
            <TH>
              <span>{t('booking.deliveryZone')}</span>
            </TH>
            <TH>
              <span>{t('booking.description')}</span>
            </TH>
          </TR>
        </THead>
        <TBody>
          {blockedBookings &&
            blockedBookings?.map((booking) => (
              <TR key={booking.id} className="booking-row">
                <TD className={`booking-row__bordered booking-row__bordered--${deliveryZoneColorMap[booking.creationIndex]}`}>
                  {booking.title ?? '--'}
                </TD>
                <TD>{booking.date}</TD>
                <TD>
                  <span className="text-nowrap">{`${booking.startTime.slice(0, 5)} - ${booking.endTime.slice(0, 5)}`}</span>
                </TD>
                <TD>
                  <Badge icon={booking.creationIndex} label={booking.deliveryZoneName} color={deliveryZoneColorMap[booking.creationIndex]} />
                </TD>
                <TD>{booking.description ?? '--'}</TD>
              </TR>
            ))}
        </TBody>
      </Table>
    </Modal>
  );
};

export default CancelAllBookingsModal;
